import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Plus, Calendar, Users, Target, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { GET_PROJECTS } from '../../graphql/queries';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { endedAs, Phase, phaseOf } from '../reach/campaignPhase';
import { briefExcerpt, calendarDate, shortDate } from '../reach/reach';

export interface Project {
  id: string;
  name: string;
  plan: string;
  deadline: string;
  status: 'planning' | 'active' | 'review' | 'completed';
  progress: number;
  /** Where it is, read exactly as Reach → Campaigns reads it. */
  phase: Phase;
  phaseLabel: string;
  postsTotal: number;
  postsCompleted: number;
  teamMembers: string[];
  color: string;
}

interface ProjectsDashboardProps {
  onCreateProject: () => void;
  onViewProject: (projectId: string) => void;
}

export function ProjectsDashboard({ onCreateProject, onViewProject }: ProjectsDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { accountId } = useAccount();

  // Fetch projects
  const { data, loading, error } = useQuery(GET_PROJECTS, {
    variables: { accountId },
    skip: !accountId,
  });

  // Transform GraphQL data to component format
  const projects: Project[] =
    // Dated campaigns only; always-on ones live in Reach → Campaigns.
    data?.projects
      ?.filter((p: any) => p.kind !== 'always_on')
      .map((p: any) => {
        const postsTotal = p.posts?.length || 0;
        const postsCompleted =
          p.posts?.filter((post: any) => post.status === 'published' || post.status === 'scheduled')
            .length || 0;
        const teamMembers = p.teamMembers?.map((tm: any) => tm.user?.email || '') || [];

        const dated = { status: p.status, deadline: p.deadline, posts: p.posts || [] };
        const phase = phaseOf(dated);
        return {
          id: p.id,
          name: p.name,
          // The card's line: the brief's opening in plain words, not its raw text.
          plan: briefExcerpt(p.campaignStory || p.plan),
          deadline: p.deadline,
          phase,
          phaseLabel:
            phase === 'ended'
              ? `Ended · ${endedAs(dated).tag}`
              : phase === 'running'
                ? 'Running'
                : 'Upcoming',
          status: p.status.toLowerCase() as 'planning' | 'active' | 'review' | 'completed',
          progress: p.progress || 0,
          postsTotal,
          postsCompleted,
          teamMembers,
          color: p.color || 'bg-cmp-bronze',
        };
      }) || [];

  const phaseColor = (phase: Phase) =>
    phase === 'running'
      ? 'bg-cmp-surface text-cmp-ink border border-cmp-line'
      : 'bg-cmp-surface text-cmp-ink-2';

  /** "Ends Oct 31 · 12 days left", "Ended Oct 30": the end is a calendar day. */
  const endLine = (project: Project) => {
    const end = calendarDate(project.deadline);
    if (!end) return '';
    if (project.phase === 'ended') return `Ended ${shortDate(end)}`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((end.getTime() - today.getTime()) / 86400000);
    return `Ends ${shortDate(end)} · ${
      days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'} left`
    }`;
  };

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.phase === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8 flex items-center justify-center">
        <Loader label="Loading campaigns" className="text-cmp-ink-3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8 flex items-center justify-center">
        <p className="text-red-500">Your campaigns didn’t load: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8">
      <div className="max-w-7xl px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="mb-2">Campaign posts</h1>
              <p>Add and edit the posts in each campaign</p>
            </div>
            <Button onClick={onCreateProject} className="gap-2">
              <Plus className="w-4 h-4" />
              New campaign
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
              <Input
                placeholder="Search campaigns…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-form-dark"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="ended">Ended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Campaigns</p>
                  <p>{projects.length}</p>
                </div>
                <Target className="w-9 h-9 text-cmp-ink-3" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Running</p>
                  <p>{projects.filter((p) => p.phase === 'running').length}</p>
                </div>
                <div className="w-9 h-9 bg-cmp-bronze-soft rounded-full flex items-center justify-center">
                  <div className="w-[14.4px] h-[14.4px] bg-cmp-bronze rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Posts</p>
                  <p>{projects.reduce((sum, p) => sum + p.postsTotal, 0)}</p>
                </div>
                <div className="w-9 h-9 bg-cmp-bronze-soft rounded-full flex items-center justify-center">
                  <div className="w-[14.4px] h-[14.4px] bg-cmp-bronze rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Team members</p>
                  <p>{new Set(projects.flatMap((p) => p.teamMembers)).size}</p>
                </div>
                <Users className="w-9 h-9 text-cmp-ink-3" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            return (
              <Card
                key={project.id}
                className="hover:shadow-lg transition-shadow cursor-pointer bg-panel border border-cmp-line"
                onClick={() => onViewProject(project.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className={`w-1 h-12 ${project.color} rounded-full`}></div>
                  </div>
                  <CardTitle className="mb-2">{project.name}</CardTitle>
                  <CardDescription className="line-clamp-2">{project.plan}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Status & Deadline */}
                    <div className="flex items-center justify-between">
                      <Badge className={phaseColor(project.phase)} variant="secondary">
                        {project.phaseLabel}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{endLine(project)}</span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} />
                    </div>

                    {/* Posts */}
                    <div className="flex items-center justify-between">
                      <span>Posts</span>
                      <span>
                        {project.postsCompleted} / {project.postsTotal}
                      </span>
                    </div>

                    {/* Team */}
                    <div className="flex items-center justify-between">
                      <span>Team</span>
                      <div className="flex -space-x-2">
                        {project.teamMembers.slice(0, 3).map((member, idx) => (
                          <div
                            key={idx}
                            className="w-10 h-10 rounded-full bg-cmp-bronze-soft text-cmp-bronze-2 border-2 flex items-center justify-center text-sm font-semibold relative flex-shrink-0"
                            style={{
                              zIndex: 10 - idx,
                              borderColor: '#373939',
                            }}
                          >
                            {member.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {project.teamMembers.length > 3 && (
                          <div
                            className="w-10 h-10 rounded-full bg-cmp-line border-2 flex items-center justify-center text-cmp-ink-2 text-sm font-semibold relative flex-shrink-0"
                            style={{
                              zIndex: 0,
                              borderColor: '#373939',
                            }}
                          >
                            +{project.teamMembers.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-cmp-ink-3">No campaigns match.</p>
          </div>
        )}
      </div>
    </div>
  );
}
